import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2, RefreshCw, Check, Users, Mail, Phone, ChevronRight, PowerOff, CreditCard,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (cents) =>
    cents == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);

const BRAND_LABEL = {
    visa: "Visa", mastercard: "Mastercard", amex: "Amex", discover: "Discover",
    diners: "Diners", jcb: "JCB", unionpay: "UnionPay",
};

/**
 * AutoRenews — owner-only roster of everyone still on the OLD auto-renew
 * pricing (member_auto_* session packs + legacy auto-renew credits). Lets the
 * owner see who's on it and switch each one OFF before it re-charges. Turning
 * it off keeps their current sessions — it only stops the next auto-charge.
 * Sachin 2026-09.
 */
export default function AutoRenews() {
    const [data, setData] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const load = () => {
        axios.get(`${API}/coach/auto-renews/roster`, { withCredentials: true })
            .then((r) => setData(r.data))
            .catch((e) => {
                toast.error("Couldn't load auto-renews", { description: e?.response?.data?.detail || "Try again." });
                setData({ clients: [], client_count: 0, total_auto_renews: 0 });
            });
    };
    useEffect(() => { load(); }, []);

    const turnOff = async (client, item) => {
        const id = item.pack_id || item.credit_id;
        setBusyId(id);
        try {
            if (item.kind === "credit") {
                await axios.post(
                    `${API}/coach/clients/${client.user_id}/credits/${item.credit_id}/auto-renew/toggle`,
                    { auto_renew: false }, { withCredentials: true },
                );
            } else {
                await axios.post(
                    `${API}/coach/clients/${client.user_id}/packs/${item.pack_id}/auto-renew/toggle`,
                    { enabled: false }, { withCredentials: true },
                );
            }
            toast.success("Auto-renew turned off", {
                description: `${client.name} · ${item.label} — sessions stay, no more auto-charge`,
            });
            load();
        } catch (e) {
            toast.error("Couldn't turn it off", { description: e?.response?.data?.detail || "Try again." });
        } finally {
            setBusyId(null);
        }
    };

    if (data === null) {
        return (
            <div className="flex items-center justify-center p-8 text-zinc-400">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading auto-renews…
            </div>
        );
    }

    const clients = data.clients || [];

    return (
        <div data-testid="auto-renews-page" className="max-w-3xl mx-auto p-6">
            <div className="mb-6 flex items-start justify-between gap-3">
                <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-1">Old auto-renew pricing</div>
                    <h1 className="font-display text-2xl tracking-wider">Auto-renews</h1>
                    <p className="text-xs text-zinc-400 mt-1 max-w-xl">
                        Everyone still on the old auto-renew packs. Tap a name to open their profile,
                        or hit <span className="text-white">Turn off</span> to stop the next auto-charge.
                        Turning it off keeps their current sessions — it only stops the re-buy.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="auto-renews-refresh"
                    onClick={load}
                    className="text-[10px] tracking-[0.15em] uppercase inline-flex items-center gap-1.5 border border-zinc-700 text-zinc-300 hover:border-brand-red px-2.5 py-1.5"
                >
                    <RefreshCw className="w-3 h-3" /> Refresh
                </button>
            </div>

            <div
                data-testid="auto-renews-summary"
                className="mb-5 text-[11px] tracking-[0.15em] uppercase text-zinc-400"
            >
                {data.total_auto_renews} auto-renew{data.total_auto_renews === 1 ? "" : "s"} · {data.client_count} client{data.client_count === 1 ? "" : "s"}
            </div>

            {clients.length === 0 ? (
                <div
                    data-testid="auto-renews-empty"
                    className="border border-emerald-800/50 bg-emerald-950/20 rounded-xl p-5 text-emerald-300 text-sm flex items-center gap-2"
                >
                    <Check className="w-4 h-4" /> Nobody is on auto-renew anymore.
                </div>
            ) : (
                <div className="space-y-3">
                    {clients.map((c) => (
                        <div
                            key={c.user_id}
                            data-testid={`auto-renew-client-${c.user_id}`}
                            className="border border-zinc-800 rounded-lg overflow-hidden bg-zinc-950/50"
                        >
                            <Link
                                to={`/coach/leads/${c.user_id}`}
                                data-testid={`auto-renew-open-${c.user_id}`}
                                className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-zinc-900/60 transition-colors group border-b border-zinc-900"
                            >
                                <div className="min-w-0">
                                    <div className="text-sm text-white truncate group-hover:text-brand-red transition-colors">
                                        {c.name}
                                    </div>
                                    {c.athletes?.length > 0 && (
                                        <div className="text-[11px] text-zinc-500 truncate mt-0.5 flex items-center gap-1">
                                            <Users className="w-3 h-3 flex-shrink-0" /> {c.athletes.join(" · ")}
                                        </div>
                                    )}
                                    <div className="text-[10px] text-zinc-600 mt-0.5 flex items-center gap-3 flex-wrap">
                                        {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                                        {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                                    </div>
                                    <div className="mt-1" data-testid={`auto-renew-card-${c.user_id}`}>
                                        {c.card?.last4 ? (
                                            <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-emerald-300">
                                                <CreditCard className="w-3 h-3" />
                                                {(BRAND_LABEL[(c.card.brand || "").toLowerCase()] || c.card.brand || "Card")} •••• {c.card.last4}
                                                {c.card.exp_month && c.card.exp_year && (
                                                    <span className="text-zinc-500">
                                                        {" · "}exp {String(c.card.exp_month).padStart(2, "0")}/{String(c.card.exp_year).slice(-2)}
                                                    </span>
                                                )}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 text-[11px] text-amber-400">
                                                <CreditCard className="w-3 h-3" /> No card on file
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <span className="text-[10px] tracking-[0.15em] uppercase text-zinc-400 inline-flex items-center gap-1 flex-shrink-0">
                                    Open <ChevronRight className="w-3 h-3" />
                                </span>
                            </Link>
                            <div className="divide-y divide-zinc-900">
                                {c.items.map((item) => {
                                    const id = item.pack_id || item.credit_id;
                                    return (
                                        <div key={id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                                            <div className="min-w-0">
                                                <div className="text-[13px] text-white truncate">{item.label}</div>
                                                <div className="text-[10px] text-zinc-500 mt-0.5">
                                                    {item.remaining_sessions} left · renews at {fmtMoney(item.renewal_price_cents)}
                                                    {item.last_renewal_failed_reason ? " · last renewal failed" : ""}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                data-testid={`auto-renew-off-${id}`}
                                                onClick={() => turnOff(c, item)}
                                                disabled={busyId === id}
                                                className="text-[10px] tracking-[0.15em] uppercase inline-flex items-center gap-1.5 border border-rose-700/60 text-rose-300 hover:bg-rose-950/30 px-2.5 py-1.5 flex-shrink-0 disabled:opacity-50"
                                            >
                                                {busyId === id ? <Loader2 className="w-3 h-3 animate-spin" /> : <PowerOff className="w-3 h-3" />} Turn off
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
